import React, {FC, useCallback, useEffect} from "react";
import {Box, Drawer, Stack, SxProps, Typography, useMediaQuery, useTheme} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import PauseStreakDrawerContent from "./PauseStreakDrawerContent";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import {useUpdateStreakState} from "@/app2/shared/data";
import { App2NotificationContext, App2NotificationSeverity } from "@/app2/shared/contexts/App2NotificationContext";

interface StreakDrawerProps {
	open: boolean;
	streakDays: number;
	isPaused: boolean;
	onClose: (data?: any) => void;
}

const PauseStreakDrawer: FC<StreakDrawerProps> = ({
	open,
	streakDays,
	isPaused,
	onClose
}) => {
	const theme = useTheme();
	const {t} = useSSTranslation();
	const { notify } = React.useContext(App2NotificationContext);
	const updateStreakMutation = useUpdateStreakState();
	const isEqualsOrGreaterThanLarge = useMediaQuery(theme.breakpoints.up("md"));
	const buttonFullWidth = !isEqualsOrGreaterThanLarge;

	const streakLabel =
        streakDays === 1 ? t("streak_day", "day streak") : t("streak_days", "day streak");

	const drawerStyle : SxProps = {
		"& .MuiDrawer-paper": {
			borderTopLeftRadius: isEqualsOrGreaterThanLarge ? 0 : "32px",
			borderTopRightRadius: isEqualsOrGreaterThanLarge ? 0 : "32px"
		}
	}

	const contentWrapperStyle: SxProps = {
		height: isEqualsOrGreaterThanLarge ? "100vh" : "calc(100vh - 64px)",
		backgroundColor: theme.palette.background.paperElevation2,
		justifyContent: "center",
		borderTop: isEqualsOrGreaterThanLarge ? `5px solid ${theme.palette.primary.main}` : undefined,
		position: "relative"
	}

	const headerStyle: SxProps = {
		width: "100%",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "25px 0",
		position: "absolute",
		top: 0
	}

	const iconStyle: SxProps = {
		marginRight: "25px"
	}

	const buttonWrapperStyle: SxProps = {
		justifyContent: "center",
		alignItems: "center",
		height: "106px",
		borderTop: isEqualsOrGreaterThanLarge ? `1px solid ${theme.palette.divider}` : "none",
		padding: "0 25px"
	}

	const buttonStyle: SxProps = {
		color: theme.palette.primary.contrastText,
		fontSize: "15px",
		height: "42px"
	}

	const congratsLabelStyle: SxProps = {
		fontSize: "24px",
		fontWeight: 400,
		paddingLeft: "20px"
	}

	const footerStyle: SxProps = {
		position: "fixed",
		bottom: 0,
		width: "100%",
		paddingBottom: isEqualsOrGreaterThanLarge ? 0 : "25px"
	}

	const handleSubmitApi = useCallback(() => {
		if(!open || updateStreakMutation.isPending)
			return;

		updateStreakMutation.mutate(
			{isStreakPaused: !isPaused},
			{
				onSuccess: (data) => {
					if (data.isStreakPaused){
						notify(
							t("streak_paused_message","Your streak is paused, but your due dates will continue as scheduled."),
							"Success",
							App2NotificationSeverity.success,
							5000
						);
					} else {
						notify(
							t("streak_unpaused_message","Streak pause removed. "),
							"Success",
							App2NotificationSeverity.success,
							5000
						);
					}
					onClose(data);
				}
			}
		);
	}, [isPaused, open, updateStreakMutation, notify, t, onClose]);

	return (
		<Drawer
			anchor="bottom"
			open={open}
			sx={drawerStyle}
		>
			<Stack sx={contentWrapperStyle}>
				<Stack direction="row" sx={headerStyle}>
					<Box sx={congratsLabelStyle}>
					</Box>
					<IconButton onClick={() => onClose()} sx={iconStyle}>
						<CloseIcon/>
					</IconButton>
				</Stack>
				<Box sx={{justifyContent: "center", alignItems: "center", alignSelf: "center", marginBottom: '140px'}}> {/*marginBottom is because campfire image isn't balanced so it needs some tweaking to be a little off-center */}
					{isPaused && <Box sx={{backgroundColor: theme.palette.primary.main, alignSelf: "center", borderRadius: "100px", padding: "0px 10px"}}>
						<Typography variant="h6" sx={{color: "#232323", textAlign: "center", fontWeight: "600"}}>
							{t("streak_paused", "Streak Paused")}
						</Typography>
					</Box>}
					<Box sx={{opacity: isPaused ? 0.4 : 1}}>
						<PauseStreakDrawerContent streakDays={streakDays} width={"285px"} height={"272px"}/>
					</Box>
					<Typography sx={{opacity: isPaused ? 0.4 : 1, color: theme.palette.text.primary, textAlign: "center", fontSize: "34px", fontWeight: 600, mt: 2}}>
						<span style={{color: theme.palette.primary.main}}>
							{streakDays}
						</span>{" "}
						{streakLabel}
					</Typography>
				</Box>
				<Box sx={footerStyle}>
					<Stack direction="row" sx={buttonWrapperStyle}>
						<LoadingButton
							variant="contained"
							fullWidth={buttonFullWidth}
							sx={buttonStyle}
							onClick={() => handleSubmitApi()}
							loading={updateStreakMutation.isPending}
						>
							{isPaused ? t("streak_unpause", "Unpause Streak").toUpperCase() : t("streak_pause", "Pause Streak").toUpperCase() }
						</LoadingButton>
					</Stack>
				</Box>
			</Stack>
		</Drawer>
	)
}

export default PauseStreakDrawer;

